import type { TourStep } from '@/components/ui/Tour';
import {
  getUnifiedManagementNavItems,
  type UnifiedManagementNavOptions,
} from '@/components/layout/sidebar-nav-config';
import { TOUR_STEP_IDS } from './tour-constants';

function StepContent({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h3 className="mb-1 font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

const DASHBOARD_NAV_TOUR_CONTENT: Record<string, { title: string; description: string }> = {
  'manage-lessons': {
    title: 'Calendar',
    description:
      'View and manage lessons across terms, camps, and private packages. Create sessions and check day-to-day schedule activity.',
  },
  'manage-courts': {
    title: 'Courts & locations',
    description:
      'Add courts and locations at your facility. Lessons, court hire, and play events all schedule on these courts.',
  },
  'manage-play': {
    title: 'Play events',
    description:
      'Run social play, round robins, and tournaments with online registration and court holds.',
  },
  'manage-staff': {
    title: 'Manage Your Coaches',
    description:
      'Add coach records for scheduling and attendance. Assign them to lessons on your calendar.',
  },
  'manage-staff-portal': {
    title: 'Manage Your Coaches',
    description:
      'Invite coaches so they can log in, view their schedule, and track students. Assign them to lessons from the calendar.',
  },
  'manage-programs': {
    title: 'Development programs',
    description:
      'Create skill pathways, track progress, and link programs to lessons and camps.',
  },
  'manage-terms': {
    title: 'Create Terms',
    description:
      'Organise your year into terms (semesters, seasons, etc.). Lessons are grouped within terms for easy management.',
  },
  'manage-camps': {
    title: 'Camps',
    description:
      'Create holiday and school-break camps, run the bulk wizard for multi-week schedules, and manage daily or full-camp bookings.',
  },
  'manage-bookings': {
    title: 'Bookings',
    description:
      'Review and manage bookings, attendance, waitlists, and refund requests — all in one place.',
  },
  'manage-payments': {
    title: 'Payments',
    description:
      'Track payments, payouts, and financial activity for your academy when payment processing is enabled.',
  },
  'manage-students': {
    title: 'Students',
    description: 'View and manage your student roster, profiles, and academy relationships.',
  },
  'manage-academy-settings': {
    title: 'Academy settings',
    description:
      'Set your academy name and age groups, then review advanced lesson policies on Professional or Enterprise.',
  },
  'manage-organization': {
    title: 'Set Up Your Organization',
    description:
      'Add contact details, public site branding, sports, and player ratings so families can find you and match play stays organized.',
  },
  'club-nav-court-hire': {
    title: 'Court hire',
    description: 'Manage walk-up and online court bookings, pricing, and availability.',
  },
  'club-nav-members': {
    title: 'Members',
    description: 'Manage member rosters, membership plans, and renewals for your club.',
  },
  'club-nav-inventory': {
    title: 'Inventory',
    description: 'Track hireable equipment and stock levels for court add-ons and loans.',
  },
};

function buildNavTourStep(tourId: string, hasCoachPortalAccess: boolean): TourStep | null {
  const contentKey =
    tourId === 'manage-staff' && hasCoachPortalAccess ? 'manage-staff-portal' : tourId;
  const copy = DASHBOARD_NAV_TOUR_CONTENT[contentKey];
  if (!copy) return null;

  return {
    content: <StepContent title={copy.title} description={copy.description} />,
    selectorId: tourId,
    position: 'right',
  };
}

export interface AcademyOwnerDashboardTourOptions extends UnifiedManagementNavOptions {
  hasCoachPortalAccess?: boolean;
}

function buildDashboardContentTourSteps(hasCoachPortalAccess: boolean): TourStep[] {
  const steps: TourStep[] = [];

  if (hasCoachPortalAccess) {
    steps.push({
      content: (
        <StepContent
          title="Coach dashboard"
          description="Switch between academy management and the coach dashboard to preview what coaches see — schedule, attendance, and student rosters."
        />
      ),
      selectorId: TOUR_STEP_IDS.DASHBOARD_MODE_SWITCH,
      position: 'bottom',
    });
  }

  steps.push(
    {
      content: (
        <StepContent
          title="Today's schedule"
          description="See lessons, court hire, and other facility activity scheduled for today."
        />
      ),
      selectorId: TOUR_STEP_IDS.TODAYS_SCHEDULE,
      position: 'top',
    },
    {
      content: (
        <StepContent
          title="Recent activity"
          description="Stay on top of new bookings, payments, and other updates as they happen."
        />
      ),
      selectorId: TOUR_STEP_IDS.RECENT_ACTIVITY,
      position: 'top',
    },
    {
      content: (
        <StepContent
          title="Getting Started Checklist"
          description="When it appears on your dashboard, use this checklist to finish core setup — academy details, courts, staff, terms, and lessons."
        />
      ),
      selectorId: TOUR_STEP_IDS.SETUP_CHECKLIST,
      position: 'top',
    },
  );

  return steps;
}

function buildNavTourSteps(
  navOptions: AcademyOwnerDashboardTourOptions,
  hasCoachPortalAccess: boolean
): TourStep[] {
  const navItems = getUnifiedManagementNavItems(navOptions).filter((item) => item.tourId);
  const steps: TourStep[] = [];

  for (const item of navItems) {
    if (!item.tourId) {
      continue;
    }

    const step = buildNavTourStep(item.tourId!, hasCoachPortalAccess);
    if (step) steps.push(step);
  }

  return steps;
}

const INTRO_TOUR_STEPS: TourStep[] = [
  {
    content: (
      <StepContent
        title="Business Overview"
        description="Keep an eye on your key metrics at a glance — academies, active courts, lessons, and revenue potential."
      />
    ),
    selectorId: TOUR_STEP_IDS.BUSINESS_OVERVIEW,
    position: 'bottom',
  },
  {
    content: (
      <StepContent
        title="Management Tools"
        description="These are your shortcuts to manage every aspect of your academy. Click any item to jump straight into that section."
      />
    ),
    selectorId: TOUR_STEP_IDS.MANAGEMENT_TOOLS,
    position: 'right',
  },
];

/** Dashboard spotlight tour for academy operators — nav steps follow unified sidenav order. */
export function getAcademyOwnerDashboardTourSteps(
  navOptions: AcademyOwnerDashboardTourOptions
): TourStep[] {
  const hasCoachPortalAccess = navOptions.hasCoachPortalAccess ?? false;
  const navSteps = buildNavTourSteps(navOptions, hasCoachPortalAccess);

  return [
    ...INTRO_TOUR_STEPS,
    ...navSteps,
    ...buildDashboardContentTourSteps(hasCoachPortalAccess),
  ];
}
